import type { MemberPreviewDay } from './memberPreviewTypes';

export interface MonthCell {
  date: string;
  day: number;
  enabled: boolean;
}

const pad = (value: number) => String(value).padStart(2, '0');

export function monthGrid(month: string, days: MemberPreviewDay[]): (MonthCell | null)[][] {
  const [year, monthNumber] = month.split('-').map(Number);
  const enabled = new Set(days.filter((day) => day.enabled).map((day) => day.date));
  const leading = new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay();
  const length = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const cells: (MonthCell | null)[] = Array.from({ length: leading }, () => null);
  for (let dayNumber = 1; dayNumber <= length; dayNumber += 1) {
    const date = `${month}-${pad(dayNumber)}`;
    cells.push({ date, day: dayNumber, enabled: enabled.has(date) });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (MonthCell | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7));
  return weeks;
}

export function previewMonths(days: MemberPreviewDay[]): string[] {
  return [...new Set(days.map((day) => day.date.slice(0, 7)))].sort();
}

export function firstEnabledDate(days: MemberPreviewDay[]): string | null {
  return days.find((day) => day.enabled && day.slots.length > 0)?.date ?? days.find((day) => day.enabled)?.date ?? null;
}

export function formatSlotTime(startAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone })
    .format(new Date(startAt))
    .replace(/\s/u, '') // ICU puts a narrow no-break space before AM/PM
    .toLowerCase();
}

export function formatConflictDate(startAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone }).format(new Date(startAt));
}

export function formatSelectedDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day))
  );
}

export function seriesSummary(durationMinutes: number, appointmentCount: number | null, cadenceWeeks: number | null): string {
  if (!appointmentCount || !cadenceWeeks) return `${durationMinutes} min`;
  return `${durationMinutes} min · ${appointmentCount} sessions / ${appointmentCount * cadenceWeeks} weeks`;
}
